// Operation Hub - Gestion des Installateurs / Poseurs
// Uniformisé sur OHGrid v1.1.1 (GROOT UI-002/004/005/006/007)
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useGovernance } from "../../hooks/useGovernance";
import { useSiretSearch } from "../../hooks/useSiretSearch";
import { normalizeIdentifier } from "../../lib/normalize";
import {
  Wrench, Plus, Loader2, Mail, Phone, MapPin,
  CheckCircle, XCircle, Building2, User, Briefcase, Trash2, Archive, ArchiveRestore,
  Eye, Edit3, Search, AlertTriangle,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { OHGrid } from "../../components/ui/oh-grid";
import { usePermissions } from "../../contexts/PermissionContext";
import { normalizeName } from "../../lib/normalize";
import { getInstallateurDisplayName } from "../../utils/installateurDisplay";

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const INSTALLATEUR_ICON_MAP = { interne: User, externe: Building2 };
const INSTALLATEUR_STYLE_MAP = {
  interne: { color: "text-blue-400", bgColor: "bg-blue-500/20", borderColor: "border-blue-500/30" },
  externe: { color: "text-amber-400", bgColor: "bg-amber-500/20", borderColor: "border-amber-500/30" },
};

// ─── Dialog de création / édition ─────────────────────────────────────────
const InstallateurDialog = ({ open, onClose, installateur, onSave, onDelete, installateurTypes }) => {
  const [form, setForm] = useState({
    nom: "", type: "externe", equipe: "", entreprise: "", siret: "",
    representant: "", rge_numero: "", rge_validite: "",
    assurance_decennale: "", assurance_numero: "", assurance_validite: "",
    email: "", telephone: "", zones: "", specialites: "", status: "actif", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [equipes, setEquipes] = useState([]);
  const [showNewEquipe, setShowNewEquipe] = useState(false);
  const [newEquipeName, setNewEquipeName] = useState("");
  // ─── Recherche SIRET / API gouv (hook partagé useSiretSearch — source unique) ───
  const { searching: siretSearching, info: siretInfo, setInfo: setSiretInfo, searchDebounced: searchSiretDebounced } = useSiretSearch();

  // ─── Création de compte (F2-bis) + Renvoyer invitation (F2-bis +) ───
  const { isAdmin } = usePermissions();
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [resendingInvitation, setResendingInvitation] = useState(false);
  // Reflète l'état "compte créé" : nourri par installateur prop, mis à jour
  // localement après une création réussie pour que le bouton bascule sans
  // attendre un refetch.
  const [accountInfo, setAccountInfo] = useState(null); // { user_email, user_created_at }
  useEffect(() => {
    if (installateur?.user_id) {
      setAccountInfo({
        user_email: installateur.user_email || installateur.email,
        user_created_at: installateur.user_created_at,
      });
    } else {
      setAccountInfo(null);
    }
  }, [installateur]);

  const handleResendInvitation = async () => {
    if (!installateur?.id || resendingInvitation) return;
    setResendingInvitation(true);
    try {
      const res = await axios.post(
        `${API}/api/operation/installateurs/${installateur.id}/resend-invitation`,
        {},
        { headers: getAuthHeaders() }
      );
      const { email, email_sent } = res.data || {};
      if (email_sent) {
        toast.success(`Nouvelle invitation envoyée à ${email} — l'ancien mot de passe est invalidé`);
      } else {
        toast.warning(`Mot de passe régénéré pour ${email} mais email NON envoyé (vérifier config SMTP)`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (err.message || "Erreur"));
    } finally {
      setResendingInvitation(false);
    }
  };

  const handleCreateAccount = async () => {
    if (!installateur?.id || creatingAccount) return;
    setCreatingAccount(true);
    try {
      const res = await axios.post(
        `${API}/api/operation/installateurs/${installateur.id}/create-account`,
        {},
        { headers: getAuthHeaders() }
      );
      const { email, user_created_at, email_sent } = res.data || {};
      setAccountInfo({ user_email: email, user_created_at });
      toast.success(
        email_sent
          ? `Compte créé — email d'invitation envoyé à ${email}`
          : `Compte créé pour ${email} — ⚠ email d'invitation non envoyé (config SMTP)`
      );
      window.dispatchEvent(new CustomEvent("omnihub:installateurs-changed"));
      onSave?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "account_already_exists") {
        setAccountInfo({
          user_email: detail.user_email,
          user_created_at: detail.user_created_at,
        });
        toast.info(detail.message || "Compte déjà existant");
      } else {
        toast.error(typeof detail === "string" ? detail : (err.message || "Erreur"));
      }
    } finally {
      setCreatingAccount(false);
    }
  };

  useEffect(() => {
    const loadEquipes = async () => {
      try {
        const res = await axios.get(`${API}/api/operation/equipes`, { headers: getAuthHeaders() });
        setEquipes(res.data.equipes || []);
      } catch (err) { console.error("Erreur chargement équipes:", err); }
    };
    if (open) loadEquipes();
  }, [open]);

  const handleCreateEquipe = async () => {
    if (!newEquipeName.trim()) return;
    try {
      const res = await axios.post(
        `${API}/api/operation/equipes?nom=${encodeURIComponent(newEquipeName.trim())}`,
        {}, { headers: getAuthHeaders() }
      );
      if (res.data.success) {
        toast.success(`Équipe "${newEquipeName}" créée`);
        setEquipes([...equipes, { id: res.data.id, nom: newEquipeName.trim(), couleur: "var(--oh-stat-blue-text)" }]);
        setForm(f => ({ ...f, equipe: newEquipeName.trim() }));
        setShowNewEquipe(false); setNewEquipeName("");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur création équipe"); }
  };

  useEffect(() => {
    if (installateur) {
      const rge0 = (installateur.certifications_rge || [])[0] || {};
      setForm({
        nom: installateur.nom || "", type: installateur.type || "externe",
        equipe: installateur.equipe || "", entreprise: installateur.entreprise || "",
        siret: installateur.siret || "", email: installateur.email || "",
        representant: installateur.representant || "",
        rge_numero: rge0.numero || "", rge_validite: rge0.validite || rge0.date_validite || "",
        assurance_decennale: installateur.assurance_decennale || "",
        assurance_numero: installateur.assurance_numero || "",
        assurance_validite: installateur.assurance_validite || "",
        telephone: installateur.telephone || "",
        zones: (installateur.zones || []).join(", "),
        specialites: (installateur.specialites || []).join(", "),
        status: installateur.status || "actif", notes: installateur.notes || "",
      });
    } else {
      setForm({ nom: "", type: "externe", equipe: "", entreprise: "", siret: "",
        representant: "", rge_numero: "", rge_validite: "",
        assurance_decennale: "", assurance_numero: "", assurance_validite: "",
        email: "", telephone: "", zones: "", specialites: "", status: "actif", notes: "" });
    }
    setSiretInfo(null);
  }, [installateur, open]);

  // ─── Recherche SIRET (hook partagé useSiretSearch — comportement identique) ───
  // L'appel API + debounce vit dans le hook ; ici on ne fait QUE le mapping vers
  // les champs installateur (entreprise/nom contact/zones dept), inchangé.
  const handleSiretChange = (val) => {
    // #314 — normalisation À LA SAISIE : « 940 455 132 00016 » collé depuis un
    // Kbis fait 18 caractères et déclenchait un 422 (max_length=14) → page
    // blanche. Moteur unique : lib/normalize.normalizeIdentifier.
    const siret = normalizeIdentifier(val, "siret");
    setForm(f => ({ ...f, siret }));
    searchSiretDebounced(siret, (m) => {
      if (!m) return;
      setForm(f => ({
        ...f,
        siret: m.siret || val,
        entreprise: m.raison_sociale || f.entreprise,
        // Ne pas écraser nom (contact) si déjà saisi
        nom: f.nom || m.dirigeant || f.nom,
        zones: f.zones || m.dept || "",
      }));
    });
  };

  const handleSubmit = async () => {
    if (!form.nom) return;
    setSaving(true);
    try {
      // Les champs RGE plats (rge_numero/rge_validite) du formulaire sont mappés
      // vers le schéma backend certifications_rge (liste). representant passe direct.
      const { rge_numero, rge_validite, ...rest } = form;
      const payload = {
        ...rest,
        zones: form.zones.split(",").map(z => z.trim()).filter(Boolean),
        specialites: form.specialites.split(",").map(s => s.trim()).filter(Boolean),
        certifications_rge: rge_numero
          ? [{ code: "", numero: rge_numero, validite: rge_validite || null }]
          : [],
      };
      if (installateur) {
        await axios.patch(`${API}/api/operation/installateurs/${installateur.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Installateur mis à jour");
      } else {
        const res = await axios.post(`${API}/api/operation/installateurs`, payload, { headers: getAuthHeaders() });
        // Auto-création de compte (F2-bis enhancement) : le backend tente
        // créer le compte + envoyer l'invitation si email présent. Échec
        // non bloquant — on remonte juste le bon toast à l'admin.
        const r = res.data || {};
        if (r.account_created && r.email_sent) {
          toast.success(`Installateur créé — invitation envoyée à ${r.user_email}`);
        } else if (r.account_created && !r.email_sent) {
          toast.warning(`Installateur créé — compte OK mais email NON envoyé (vérifier config SMTP)`);
        } else if (r.account_skipped_reason === "no_email") {
          toast.success("Installateur créé — renseignez un email pour activer son accès");
        } else if (r.account_skipped_reason === "email_taken") {
          toast.warning(`Installateur créé — email déjà utilisé par un autre compte, lien non fait`);
        } else {
          toast.success("Installateur créé");
        }
      }
      // Invalide les caches lookup côté autres composants montés (ex: AdminBlock dropdown).
      window.dispatchEvent(new CustomEvent("omnihub:installateurs-changed"));
      onSave(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-white/10 max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Wrench className="w-5 h-5 text-orange-400" />
            {installateur ? "Modifier installateur" : "Nouvel installateur / poseur"}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {installateur ? "Modifiez les informations de cet installateur / poseur." : "Renseignez les informations du nouvel installateur ou poseur."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-4">
          <div className="space-y-2">
            <Label className="text-zinc-300">Type *</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.values(installateurTypes).map(type => {
                const TypeIcon = type.icon;
                const isSelected = form.type === type.id;
                return (
                  <button key={type.id} type="button" onClick={() => setForm({ ...form, type: type.id })}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      isSelected ? `${type.bgColor} ${type.borderColor}` : 'bg-zinc-800/50 border-white/5 hover:border-white/10'
                    }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg ${isSelected ? type.bgColor : 'bg-zinc-700'} flex items-center justify-center`}>
                        <TypeIcon className={`w-4 h-4 ${isSelected ? type.color : 'text-zinc-400'}`} />
                      </div>
                      <div>
                        <p className={`font-medium ${isSelected ? type.color : 'text-white'}`}>{type.label}</p>
                        <p className="text-xs text-zinc-500">{type.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Nom *</Label>
              <Input value={form.nom}
                onChange={e => setForm({ ...form, nom: e.target.value })}
                onBlur={e => setForm({ ...form, nom: normalizeName(e.target.value, form.type === 'interne' ? "nom" : "entreprise") })}
                className="bg-zinc-800 border-white/10 text-white"
                placeholder={form.type === 'interne' ? 'Prénom Nom' : 'Nom du contact'} />
            </div>
            {form.type === 'interne' ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-300">Équipe *</Label>
                  <Button variant="ghost" size="sm" onClick={() => setShowNewEquipe(true)} className="text-xs text-blue-400 hover:text-blue-300 h-auto py-0">
                    <Plus className="w-3 h-3 mr-1" /> Nouvelle
                  </Button>
                </div>
                {showNewEquipe ? (
                  <div className="flex gap-2">
                    <Input value={newEquipeName} onChange={e => setNewEquipeName(e.target.value)}
                      placeholder="Nom de l'équipe" className="bg-zinc-800 border-white/10 text-white flex-1"
                      onKeyPress={e => e.key === 'Enter' && handleCreateEquipe()} />
                    <Button size="sm" onClick={handleCreateEquipe} className="bg-blue-600"><CheckCircle className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowNewEquipe(false)}><XCircle className="w-4 h-4" /></Button>
                  </div>
                ) : (
                  <Select value={form.equipe} onValueChange={v => setForm({ ...form, equipe: v })}>
                    <SelectTrigger className="bg-zinc-800 border-white/10 text-white"><SelectValue placeholder="Sélectionner une équipe" /></SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-white/10">
                      {equipes.length === 0 ? (
                        <div className="px-2 py-4 text-center text-zinc-500 text-sm">Aucune équipe créée</div>
                      ) : equipes.map(eq => (
                        <SelectItem key={eq.id} value={eq.nom}>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: eq.couleur || 'var(--oh-stat-blue-text)' }} />
                            {eq.nom}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            ) : null}
          </div>

          {/* SIRET en premier (pour type externe) avec autofill API */}
          {form.type === 'externe' && (
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                N° SIRET / RCS
                <span className="text-xs text-zinc-500">(auto-remplissage via recherche-entreprises.api.gouv.fr)</span>
              </Label>
              <div className="relative">
                <Input value={form.siret} onChange={e => handleSiretChange(e.target.value)}
                  className="bg-zinc-800 border-white/10 text-white pr-10" placeholder="Ex: 91954043500014 (9 ou 14 chiffres)"
                  data-testid="installateur-siret-input" />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {siretSearching
                    ? <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                    : <Search className="w-4 h-4 text-zinc-500" />}
                </div>
              </div>
              {siretInfo && siretInfo.found && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded px-3 py-2 text-xs text-emerald-300 flex items-start gap-2"
                  data-testid="siret-found-alert">
                  <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium">{siretInfo.message}</div>
                    {siretInfo.siege_adresse && (
                      <div className="text-emerald-200/70 mt-0.5">Siège : {siretInfo.siege_adresse}</div>
                    )}
                  </div>
                </div>
              )}
              {siretInfo && !siretInfo.found && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2 text-xs text-amber-300 flex items-start gap-2"
                  data-testid="siret-not-found-alert">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>{siretInfo.message}</div>
                </div>
              )}
              <p className="text-xs text-zinc-500">Empêche les doublons d'installateurs</p>
            </div>
          )}

          {/* Nom entreprise (type externe) — rempli auto par SIRET, modifiable */}
          {form.type === 'externe' && (
            <div className="space-y-2">
              <Label className="text-zinc-300">Entreprise</Label>
              <Input value={form.entreprise}
                onChange={e => setForm({ ...form, entreprise: e.target.value })}
                onBlur={e => setForm({ ...form, entreprise: normalizeName(e.target.value, "entreprise") })}
                className="bg-zinc-800 border-white/10 text-white" placeholder="Nom de la société (auto-rempli via SIRET)" />
            </div>
          )}

          {/* Sous-traitance (devis OMV) — président + RGE. Externe uniquement. */}
          {form.type === 'externe' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Président / représentant</Label>
                <Input value={form.representant}
                  onChange={e => setForm({ ...form, representant: e.target.value })}
                  className="bg-zinc-800 border-white/10 text-white"
                  placeholder="Nom du représentant légal" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">N° RGE</Label>
                <Input value={form.rge_numero}
                  onChange={e => setForm({ ...form, rge_numero: e.target.value })}
                  className="bg-zinc-800 border-white/10 text-white" placeholder="Ex : E-E123456" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Validité RGE</Label>
                <Input type="date" value={form.rge_validite}
                  onChange={e => setForm({ ...form, rge_validite: e.target.value })}
                  className="bg-zinc-800 border-white/10 text-white" />
              </div>
            </div>
          )}

          {/* Assurance décennale (externe / sous-traitant) — mentions devis CEE. */}
          {form.type === 'externe' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Assurance décennale</Label>
                <Input value={form.assurance_decennale}
                  onChange={e => setForm({ ...form, assurance_decennale: e.target.value })}
                  className="bg-zinc-800 border-white/10 text-white" placeholder="Nom de l'assureur" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">N° police</Label>
                <Input value={form.assurance_numero}
                  onChange={e => setForm({ ...form, assurance_numero: e.target.value })}
                  className="bg-zinc-800 border-white/10 text-white" placeholder="N° de police" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Validité assurance</Label>
                <Input type="date" value={form.assurance_validite}
                  onChange={e => setForm({ ...form, assurance_validite: e.target.value })}
                  className="bg-zinc-800 border-white/10 text-white" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-zinc-800 border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Téléphone</Label>
              <Input value={form.telephone} onChange={e => setForm({ ...form, telephone: e.target.value })} className="bg-zinc-800 border-white/10 text-white" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300">Zones d'intervention (séparées par virgules)</Label>
            <Input value={form.zones} onChange={e => setForm({ ...form, zones: e.target.value })}
              placeholder="75, 92, 93, IDF" className="bg-zinc-800 border-white/10 text-white" />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300">Spécialités (séparées par virgules)</Label>
            <Input value={form.specialites} onChange={e => setForm({ ...form, specialites: e.target.value })}
              placeholder="Pompe à chaleur, Isolation, Panneaux solaires" className="bg-zinc-800 border-white/10 text-white" />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300">Statut</Label>
            <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
              <SelectTrigger className="bg-zinc-800 border-white/10 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-800 border-white/10">
                <SelectItem value="actif" className="text-white">Actif</SelectItem>
                <SelectItem value="inactif" className="text-white">Inactif</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300">Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="bg-zinc-800 border-white/10 text-white min-h-[60px]" />
          </div>

          {/* ─── Compte utilisateur (F2-bis) ─ Visible uniquement en édition + admin ─ */}
          {installateur && isAdmin() && (
            <div className="space-y-2 pt-4 border-t border-white/10">
              <Label className="text-zinc-300 flex items-center gap-2">
                <User className="w-4 h-4 text-cyan-400" />
                Compte utilisateur
              </Label>
              {accountInfo ? (
                <div className="flex items-center justify-between bg-zinc-800/50 border border-white/10 rounded-md px-3 py-2 gap-3">
                  <div className="text-sm min-w-0">
                    <p className="text-zinc-300">
                      Compte créé{accountInfo.user_created_at && (
                        <> le <span className="text-zinc-100 font-medium">
                          {new Date(accountInfo.user_created_at).toLocaleDateString("fr-FR", {
                            day: "2-digit", month: "short", year: "numeric"
                          })}
                        </span></>
                      )}
                    </p>
                    {accountInfo.user_email && (
                      <p className="text-xs text-zinc-500 font-mono mt-0.5 truncate">{accountInfo.user_email}</p>
                    )}
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={resendingInvitation}
                        aria-label="Renvoyer l'invitation à l'installateur"
                        data-testid="resend-invitation-btn"
                        className="border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10 shrink-0"
                      >
                        {resendingInvitation
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                          : <Mail className="w-3.5 h-3.5 mr-1.5" />}
                        Renvoyer l'invitation
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-zinc-900 border-white/10">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Renvoyer l'invitation ?</AlertDialogTitle>
                        <AlertDialogDescription className="text-zinc-400">
                          Un nouveau mot de passe temporaire va être envoyé à {accountInfo.user_email}.
                          L'ancien mot de passe sera invalidé immédiatement.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-zinc-800 border-white/10 text-white hover:bg-zinc-700">
                          Annuler
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleResendInvitation}
                          className="bg-cyan-600 hover:bg-cyan-700"
                        >
                          Renvoyer
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <Button type="button" onClick={handleCreateAccount}
                    disabled={creatingAccount || !form.email}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white">
                    {creatingAccount
                      ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      : <Mail className="w-4 h-4 mr-2" />}
                    Créer compte
                  </Button>
                  <p className="text-xs text-zinc-500 mt-2 flex-1">
                    {form.email
                      ? "Un mot de passe temporaire sera envoyé à l'email ci-dessus. Il devra être changé à la 1re connexion."
                      : "Renseignez un email avant de créer le compte."}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 border-t border-white/10 pt-4">
          {installateur && (
            <div className="flex gap-2 mr-auto">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="text-red-400 border-red-500/30 hover:bg-red-500/10" disabled={deleting}>
                    <Trash2 className="w-4 h-4 mr-2" />Supprimer
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-zinc-900 border-white/10">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">Supprimer cet installateur ?</AlertDialogTitle>
                    <AlertDialogDescription className="text-zinc-400">
                      Action irréversible. "{getInstallateurDisplayName(installateur)}" sera définitivement supprimé.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-zinc-800 border-white/10 text-white hover:bg-zinc-700">Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={async () => {
                      setDeleting(true);
                      try {
                        await axios.delete(`${API}/api/operation/installateurs/${installateur.id}`, { headers: getAuthHeaders() });
                        toast.success("Installateur supprimé");
                        window.dispatchEvent(new CustomEvent("omnihub:installateurs-changed"));
                        onDelete?.(); onClose();
                      } catch (err) { toast.error(err.response?.data?.detail || err.message); }
                      finally { setDeleting(false); }
                    }} className="bg-red-600 hover:bg-red-700">
                      {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Supprimer"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={!form.nom || saving}
            className={form.type === 'interne' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-amber-600 hover:bg-amber-700'}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {installateur ? "Modifier" : "Créer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Composant principal (migré OHGrid) ───────────────────────────────────
export default function InstallateursManager() {
  const { items: installateurTypeItems } = useGovernance("installateur_types");
  const installateurTypes = useMemo(() => {
    const m = {};
    installateurTypeItems.forEach(i => {
      m[i.id] = { ...i, icon: INSTALLATEUR_ICON_MAP[i.id] || Building2, ...(INSTALLATEUR_STYLE_MAP[i.id] || INSTALLATEUR_STYLE_MAP.externe) };
    });
    return m;
  }, [installateurTypeItems]);

  const [installateurs, setInstallateurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedInstallateur, setSelectedInstallateur] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Toujours charger tous (archivés inclus) pour le filtre client-side OHGrid
      const res = await axios.get(`${API}/api/operation/installateurs?include_archived=true&limit=200`, { headers: getAuthHeaders() });
      setInstallateurs(res.data);
    } catch (err) { console.error(err); toast.error("Erreur chargement installateurs"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const visibleRows = useMemo(
    () => installateurs
      .filter(i => showArchived || !i.archived)
      .map(i => ({
        ...i,
        // UI-only: affichage "Entreprise" avec fallback sur nom si absent
        _display_entreprise: (i.entreprise && i.entreprise.trim()) || i.nom || "",
        _display_contact: (i.entreprise && i.nom && i.entreprise.trim() !== i.nom.trim()) ? i.nom : "",
      })),
    [installateurs, showArchived]
  );

  // ─── Actions unitaires ───
  const handleArchive = useCallback(async (row, archived) => {
    try {
      await axios.post(`${API}/api/operation/installateurs/${row.id}/archive`, { archived }, { headers: getAuthHeaders() });
      toast.success(archived ? `${row.nom} archivé` : `${row.nom} réactivé`);
      window.dispatchEvent(new CustomEvent("omnihub:installateurs-changed"));
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur archivage");
    }
  }, [loadData]);

  const handleDelete = useCallback(async (row) => {
    if (!window.confirm(`Supprimer définitivement "${row.nom}" ?`)) return;
    try {
      await axios.delete(`${API}/api/operation/installateurs/${row.id}`, { headers: getAuthHeaders() });
      toast.success("Installateur supprimé");
      window.dispatchEvent(new CustomEvent("omnihub:installateurs-changed"));
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  }, [loadData]);

  // ─── Actions compte utilisateur (depuis la grille, sans ouvrir le dialog) ───
  const handleGridCreateAccount = useCallback(async (row) => {
    if (!row.email) {
      toast.error("Pas d'email — éditez l'installateur pour le renseigner d'abord.");
      return;
    }
    if (!window.confirm(`Créer un compte pour ${row.nom} et envoyer une invitation à ${row.email} ?`)) return;
    try {
      const res = await axios.post(
        `${API}/api/operation/installateurs/${row.id}/create-account`,
        {},
        { headers: getAuthHeaders() }
      );
      const { email, email_sent } = res.data || {};
      toast.success(email_sent
        ? `Compte créé — invitation envoyée à ${email}`
        : `Compte créé pour ${email} (email NON envoyé, vérifier SMTP)`);
      loadData();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || err.message));
    }
  }, [loadData]);

  const handleGridResendInvitation = useCallback(async (row) => {
    if (!window.confirm(`Renvoyer une nouvelle invitation à ${row.user_email || row.email} ? L'ancien mot de passe sera invalidé.`)) return;
    try {
      const res = await axios.post(
        `${API}/api/operation/installateurs/${row.id}/resend-invitation`,
        {},
        { headers: getAuthHeaders() }
      );
      const { email, email_sent } = res.data || {};
      toast.success(email_sent
        ? `Nouvelle invitation envoyée à ${email}`
        : `Mot de passe régénéré pour ${email} (email NON envoyé, vérifier SMTP)`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : err.message);
    }
  }, []);

  // ─── Audit + bulk create accounts (modal) ───
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/operation/installateurs-admin/audit-accounts`,
        { headers: getAuthHeaders() }
      );
      setAuditData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur audit");
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const handleOpenAudit = useCallback(() => {
    setAuditOpen(true);
    loadAudit();
  }, [loadAudit]);

  const handleBulkCreate = useCallback(async () => {
    if (!window.confirm("Créer tous les comptes pour les installateurs éligibles (email valide, libre) et envoyer les invitations ?")) return;
    setBulkRunning(true);
    try {
      const res = await axios.post(
        `${API}/api/operation/installateurs-admin/bulk-create-accounts`,
        {},
        { headers: getAuthHeaders() }
      );
      const { processed, created, skipped, email_sent_count } = res.data || {};
      toast.success(`Bulk créé : ${created} comptes, ${email_sent_count} emails envoyés, ${skipped} skipped sur ${processed}`);
      loadAudit();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur bulk-create");
    } finally {
      setBulkRunning(false);
    }
  }, [loadAudit, loadData]);

  // ─── Bulk actions ───
  const bulkArchive = useCallback(async (ids, archived) => {
    if (!window.confirm(`${archived ? "Archiver" : "Réactiver"} ${ids.length} installateur(s) ?`)) return;
    let ok = 0, errs = 0;
    for (const id of ids) {
      try {
        await axios.post(`${API}/api/operation/installateurs/${id}/archive`, { archived }, { headers: getAuthHeaders() });
        ok++;
      } catch { errs++; }
    }
    if (ok) toast.success(`${ok} installateur(s) traité(s)`);
    if (errs) toast.error(`${errs} erreur(s)`);
    if (ok) window.dispatchEvent(new CustomEvent("omnihub:installateurs-changed"));
    loadData();
  }, [loadData]);

  const bulkDelete = useCallback(async (ids) => {
    if (!window.confirm(`Supprimer ${ids.length} installateur(s) ? Irréversible.`)) return;
    let ok = 0, errs = 0;
    for (const id of ids) {
      try {
        await axios.delete(`${API}/api/operation/installateurs/${id}`, { headers: getAuthHeaders() });
        ok++;
      } catch { errs++; }
    }
    if (ok) toast.success(`${ok} installateur(s) supprimé(s)`);
    if (errs) toast.error(`${errs} erreur(s)`);
    if (ok) window.dispatchEvent(new CustomEvent("omnihub:installateurs-changed"));
    loadData();
  }, [loadData]);

  // ─── Config OHGrid ───
  const stats = useMemo(() => {
    const base = visibleRows;
    return [
      { key: "total", label: "Total", value: base.length, icon: Wrench, color: "orange", filter: null },
      { key: "internes", label: "Internes", value: base.filter(i => i.type === 'interne').length, icon: User, color: "blue",
        filter: (row) => row.type === 'interne' },
      { key: "externes", label: "Externes", value: base.filter(i => i.type === 'externe' || !i.type).length, icon: Building2, color: "yellow",
        filter: (row) => row.type === 'externe' || !row.type },
      { key: "actifs", label: "Actifs", value: base.filter(i => i.status === 'actif' && !i.archived).length, icon: CheckCircle, color: "green",
        filter: (row) => row.status === 'actif' && !row.archived },
      { key: "archived", label: "Archivés", value: installateurs.filter(i => i.archived).length, icon: Archive, color: "gray",
        filter: (row) => row.archived },
    ];
  }, [visibleRows, installateurs]);

  const advancedFilters = useMemo(() => [
    { key: "type", label: "Type", type: "select", defaultValue: "all",
      options: [{ value: "interne", label: "Interne" }, { value: "externe", label: "Externe" }] },
    { key: "status", label: "Statut", type: "select", defaultValue: "all",
      options: [{ value: "actif", label: "Actif" }, { value: "inactif", label: "Inactif" }] },
    { key: "archived", label: "Inclure archivés", type: "checkbox", defaultValue: false },
  ], []);

  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const handleAdvFilter = useCallback(({ key, value }) => {
    if (key === "archived") setShowArchived(value);
    if (key === "type") setFilterType(value);
    if (key === "status") setFilterStatus(value);
  }, []);

  const filteredRows = useMemo(() => visibleRows.filter(r => {
    if (filterType !== "all" && r.type !== filterType) return false;
    if (filterStatus !== "all" && r.status !== filterStatus) return false;
    return true;
  }), [visibleRows, filterType, filterStatus]);

  const searchFilter = useCallback((row, q) => (
    (row.nom || "").toLowerCase().includes(q) ||
    (row.entreprise || "").toLowerCase().includes(q) ||
    (row.siret || "").toLowerCase().includes(q) ||
    (row.email || "").toLowerCase().includes(q) ||
    (row.telephone || "").includes(q)
  ), []);

  const columns = useMemo(() => [
    {
      key: "_display_entreprise", label: "Entreprise",
      render: (v, row) => {
        const t = installateurTypes[row.type] || installateurTypes.externe || { icon: Building2, bgColor: "bg-amber-500/20", color: "text-amber-400", label: row.type };
        const Ic = t.icon;
        return (
          <div className="flex items-center gap-2.5">
            <div className={`w-7 h-7 rounded-lg ${t.bgColor} flex items-center justify-center shrink-0`}>
              <Ic className={`w-3.5 h-3.5 ${t.color}`} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-medium truncate" style={{ color: row.archived ? "var(--oh-text-muted)" : "var(--oh-text)" }}>{v || "—"}</span>
                {row.archived && <Badge className="text-[9px] bg-amber-500/15 text-amber-400 border-amber-500/30">Archivé</Badge>}
              </div>
              {row._display_contact && (
                <p className="text-xs truncate max-w-[220px]" style={{ color: "var(--oh-text-muted)" }}>
                  Contact : {row._display_contact}
                </p>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: "type", label: "Type",
      render: (v) => {
        const t = installateurTypes[v] || installateurTypes.externe || { bgColor: "bg-amber-500/20", color: "text-amber-400", label: v };
        return <Badge className={`${t.bgColor} ${t.color} text-xs`}>{t.label}</Badge>;
      },
    },
    {
      key: "siret", label: "SIRET",
      render: (v) => v ? <span className="font-mono text-xs text-purple-400">{v}</span> : <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>—</span>,
    },
    {
      key: "equipe", label: "Équipe", sortable: false,
      render: (v) => v ? (
        <span className="text-xs text-blue-400 flex items-center gap-1">
          <Briefcase className="w-3 h-3" /> {v}
        </span>
      ) : <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>—</span>,
    },
    {
      key: "_contact", label: "Contact", sortable: false,
      render: (_, row) => (
        <div className="space-y-0.5">
          {row.telephone && <div className="flex items-center gap-1 text-[11px]" style={{ color: "var(--oh-text-muted)" }}><Phone className="w-3 h-3" />{row.telephone}</div>}
          {row.email && <div className="flex items-center gap-1 text-[11px] truncate max-w-[180px]" style={{ color: "var(--oh-text-muted)" }}><Mail className="w-3 h-3" />{row.email}</div>}
        </div>
      ),
    },
    {
      key: "_account", label: "Compte", sortable: false,
      // Colonne dédiée au statut "compte utilisateur" + actions inline pour
      // Stéphanie qui n'a pas accès au menu kebab "..." rapidement.
      render: (_, row) => {
        if (row.user_id) {
          return (
            <div className="flex items-center gap-2">
              <Badge
                style={{ background: "rgba(16,185,129,0.15)", color: "#10b981", border: "1px solid rgba(16,185,129,0.30)" }}
                className="text-[10px]"
              >
                <CheckCircle className="w-3 h-3 mr-1" />Compte créé
              </Badge>
              <Button
                size="sm" variant="outline"
                className="h-7 px-2 text-[11px] border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10"
                onClick={(e) => { e.stopPropagation(); handleGridResendInvitation(row); }}
                aria-label={`Renvoyer l'invitation à ${row.nom}`}
                data-testid={`resend-${row.id}`}
              >
                <Mail className="w-3 h-3 mr-1" />Renvoyer
              </Button>
            </div>
          );
        }
        if (!row.email) {
          return (
            <Badge
              style={{ background: "var(--oh-bg-surface)", color: "var(--oh-text-muted)", border: "1px solid var(--oh-border)" }}
              className="text-[10px]"
            >
              <AlertTriangle className="w-3 h-3 mr-1" />Email manquant
            </Badge>
          );
        }
        return (
          <Button
            size="sm" variant="outline"
            className="h-7 px-2 text-[11px] border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10"
            onClick={(e) => { e.stopPropagation(); handleGridCreateAccount(row); }}
            aria-label={`Créer un compte pour ${row.nom} et envoyer une invitation`}
            data-testid={`create-account-${row.id}`}
          >
            <Mail className="w-3 h-3 mr-1" />Créer compte
          </Button>
        );
      },
    },
    {
      key: "zones", label: "Zones", sortable: false,
      render: (v) => Array.isArray(v) && v.length > 0 ? (
        <span className="flex items-center gap-1 text-xs" style={{ color: "var(--oh-text-muted)" }}>
          <MapPin className="w-3 h-3" /> {v.slice(0, 3).join(", ")}{v.length > 3 ? ` +${v.length - 3}` : ""}
        </span>
      ) : "—",
    },
    {
      key: "status", label: "Statut",
      render: (v) => v === 'actif'
        ? <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">Actif</Badge>
        : <Badge className="bg-zinc-500/20 text-zinc-400 text-xs">Inactif</Badge>,
    },
    {
      key: "missions_count", label: "Missions",
      render: (v) => <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{v || 0}</span>,
    },
  ], [installateurTypes, handleGridCreateAccount, handleGridResendInvitation]);

  const rowActions = useMemo(() => [
    { label: "Voir / Modifier", icon: Edit3, onClick: (row) => { setSelectedInstallateur(row); setDialogOpen(true); } },
    {
      // Toujours montré si pas de compte ; si pas d'email, le handler
      // affiche un toast clair (et redirige mentalement vers l'édition).
      label: "Créer compte + invitation",
      icon: Mail,
      onClick: handleGridCreateAccount,
      show: (row) => !row.user_id,
      className: "text-cyan-400",
    },
    {
      label: "Renvoyer l'invitation",
      icon: Mail,
      onClick: handleGridResendInvitation,
      show: (row) => !!row.user_id,
      className: "text-cyan-400",
    },
    { label: "Archiver", icon: Archive, onClick: (row) => handleArchive(row, true),
      show: (row) => !row.archived, className: "text-amber-400" },
    { label: "Réactiver", icon: ArchiveRestore, onClick: (row) => handleArchive(row, false),
      show: (row) => row.archived, className: "text-blue-400" },
    { label: "Supprimer", icon: Trash2, onClick: handleDelete, className: "text-red-400" },
  ], [handleArchive, handleDelete, handleGridCreateAccount, handleGridResendInvitation]);

  const bulkActions = useMemo(() => [
    { label: "Archiver", icon: Archive, onClick: (ids) => bulkArchive(ids, true),
      className: "border-amber-600 text-amber-400 hover:bg-amber-600/20" },
    { label: "Réactiver", icon: ArchiveRestore, onClick: (ids) => bulkArchive(ids, false),
      className: "border-blue-600 text-blue-400 hover:bg-blue-600/20" },
    { label: "Supprimer", icon: Trash2, onClick: bulkDelete,
      className: "border-red-600 text-red-400 hover:bg-red-600/20" },
  ], [bulkArchive, bulkDelete]);

  return (
    <div className="space-y-4" data-testid="installateurs-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-orange-500/10">
            <Wrench className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h1 className="text-lg font-semibold" style={{ color: "var(--oh-text)" }}>Installateurs / Poseurs</h1>
            <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
              {installateurs.length} au total · {installateurs.filter(i => i.type === 'interne').length} internes · {installateurs.filter(i => i.type === 'externe' || !i.type).length} externes
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleOpenAudit} variant="outline"
            data-testid="audit-accounts-btn"
            className="border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10"
            aria-label="Auditer les comptes utilisateurs des installateurs">
            <User className="w-4 h-4 mr-2" /> Audit comptes
          </Button>
          <Button onClick={() => { setSelectedInstallateur(null); setDialogOpen(true); }}
            className="bg-orange-600 hover:bg-orange-700" data-testid="add-installateur-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouvel installateur
          </Button>
        </div>
      </div>

      <OHGrid
        data={filteredRows}
        columns={columns}
        stats={stats}
        advancedFilters={advancedFilters}
        onAdvancedFilter={handleAdvFilter}
        rowActions={rowActions}
        bulkActions={bulkActions}
        selectable={true}
        pagination={true}
        pageSize={25}
        loading={loading}
        onRefresh={loadData}
        onRowClick={(row) => { setSelectedInstallateur(row); setDialogOpen(true); }}
        searchPlaceholder="Rechercher par nom, entreprise, SIRET, email..."
        searchFilter={searchFilter}
        defaultSortKey="_display_entreprise"
        defaultSortDir="asc"
        emptyMessage="Aucun installateur trouvé"
        testIdPrefix="installateurs"
      />

      <InstallateurDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        installateur={selectedInstallateur}
        onSave={loadData}
        onDelete={loadData}
        installateurTypes={installateurTypes}
      />

      {/* Modal Audit comptes utilisateurs (admin tools) */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] flex flex-col"
          style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
              <User className="w-5 h-5" style={{ color: "var(--oh-stat-cyan-text)" }} />
              Audit comptes installateurs
            </DialogTitle>
            <DialogDescription style={{ color: "var(--oh-text-muted)" }}>
              Liste des installateurs sans compte utilisateur et raisons.
              Le bouton "Créer les manquants" génère un compte + email pour les éligibles (email valide & libre).
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            {auditLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--oh-text-muted)" }} />
              </div>
            ) : !auditData ? (
              <p className="text-center py-8" style={{ color: "var(--oh-text-muted)" }}>Pas de données.</p>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  <div className="rounded-lg p-3 text-center"
                    style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)" }}>
                    <p className="text-2xl font-bold" style={{ color: "var(--oh-text)" }}>{auditData.total}</p>
                    <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>Total installateurs</p>
                  </div>
                  <div className="rounded-lg p-3 text-center"
                    style={{ background: "rgba(16,185,129,0.10)", border: "1px solid rgba(16,185,129,0.30)" }}>
                    <p className="text-2xl font-bold" style={{ color: "#10b981" }}>{auditData.with_account}</p>
                    <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>Avec compte</p>
                  </div>
                  <div className="rounded-lg p-3 text-center"
                    style={{ background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.30)" }}>
                    <p className="text-2xl font-bold" style={{ color: "var(--oh-stat-amber-text)" }}>{auditData.without_account}</p>
                    <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>Sans compte</p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]">
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--oh-border)" }}>
                      <th className="text-left p-2 text-xs font-medium" style={{ color: "var(--oh-text-muted)" }}>Installateur</th>
                      <th className="text-left p-2 text-xs font-medium" style={{ color: "var(--oh-text-muted)" }}>Email</th>
                      <th className="text-left p-2 text-xs font-medium" style={{ color: "var(--oh-text-muted)" }}>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditData.items.filter(i => !i.has_account).map(it => {
                      const reasonLabel = {
                        no_email: "Email manquant",
                        email_taken: "Email déjà utilisé",
                      }[it.missing_reason] || "Éligible — sera créé";
                      const reasonColor = it.missing_reason === "no_email"
                        ? "var(--oh-text-muted)"
                        : it.missing_reason === "email_taken"
                          ? "var(--oh-stat-red-text)"
                          : "#10b981";
                      return (
                        <tr key={it.installateur_id} className="border-t" style={{ borderColor: "var(--oh-border)" }}>
                          <td className="p-2" style={{ color: "var(--oh-text)" }}>{it.nom}</td>
                          <td className="p-2 text-xs" style={{ color: "var(--oh-text-secondary)" }}>{it.email || "—"}</td>
                          <td className="p-2 text-xs" style={{ color: reasonColor }}>{reasonLabel}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
                {auditData.items.filter(i => !i.has_account).length === 0 && (
                  <p className="text-center py-6" style={{ color: "var(--oh-text-muted)" }}>
                    Tous les installateurs ont un compte.
                  </p>
                )}
              </>
            )}
          </div>

          <DialogFooter className="border-t pt-3" style={{ borderColor: "var(--oh-border)" }}>
            <Button variant="outline" onClick={() => setAuditOpen(false)}>Fermer</Button>
            <Button
              onClick={handleBulkCreate}
              disabled={bulkRunning || !auditData || auditData.without_account === 0}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="bulk-create-accounts-btn"
              aria-label="Créer les comptes manquants en masse"
            >
              {bulkRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Créer les manquants
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
